import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isNativeApp } from "@/lib/platform";

/**
 * Notification bell shown in the top-right corner — Capacitor app only.
 *
 * Badge = (unread messages) + (matches that have appeared since the user
 * last opened the /matches screen). We track "seen" via a localStorage
 * counter so the badge clears without adding a `seen_matches` collection to
 * the backend.
 */
const SEEN_KEY = "notif:matchesSeenCount";

export function markMatchesSeen(total) {
    try { localStorage.setItem(SEEN_KEY, String(total)); } catch { /* ignore */ }
}

function readSeenCount() {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        const n = raw == null ? null : parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch { return null; }
}

export default function NotificationBell() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const [newMatches, setNewMatches] = useState(0);

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const [threads, m, c, b] = await Promise.all([
                api.get("/messages/threads"),
                api.get("/matches"),
                api.get("/matches/chain"),
                api.get("/matches/buddies").catch(() => ({ data: [] })),
            ]);
            const unreadTotal = (threads.data || []).reduce((n, t) => n + (t.unread || 0), 0);
            const totalMatches = (m.data?.length || 0) + (c.data?.length || 0) + (b.data?.length || 0);
            const seen = readSeenCount();
            // On the very first load we bootstrap "seen" to the current count so
            // users don't see a huge red badge for matches they've already checked.
            if (seen == null) {
                markMatchesSeen(totalMatches);
                setNewMatches(0);
            } else {
                setNewMatches(Math.max(0, totalMatches - seen));
            }
            setUnread(unreadTotal);
        } catch { /* silent — the bell degrades gracefully if the API blips */ }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        load();
        const t = setInterval(load, 45000);
        // Re-check when the tab/app becomes visible again (e.g., returning from
        // background) so the badge stays fresh without a manual refresh.
        const onVis = () => { if (document.visibilityState === "visible") load(); };
        document.addEventListener("visibilitychange", onVis);
        return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
    }, [user, load]);

    if (!isNativeApp() || !user) return null;

    const count = unread + newMatches;
    // If only new matches are pending (no unread messages), we route to the
    // Matches screen so the user lands where the notification came from.
    const target = unread === 0 && newMatches > 0 ? "/matches" : "/messages";

    return (
        <button
            data-testid="nav-notification-bell"
            onClick={() => navigate(target)}
            aria-label={count > 0 ? `${count} new items` : "Notifications"}
            className="relative w-10 h-10 rounded-full bg-white border border-border/70 flex items-center justify-center hover:bg-muted active:scale-95 transition-transform shadow-sm"
        >
            <Bell className="w-5 h-5" strokeWidth={2.2} />
            {count > 0 && (
                <span
                    data-testid="notification-bell-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background"
                >
                    {count > 99 ? "99+" : count}
                </span>
            )}
        </button>
    );
}
